"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";

// First-touch attribution. PostHog auto-captures UTMs on every $pageview, but
// those are *last* touch; for funnel analysis we need the original landing
// context to survive the anonymous-to-identified merge that happens on login.
//
// Mounted at the root rather than in the marketing layout, because first touch
// has to mean first touch ANYWHERE. It used to run only under (marketing), so
// anyone whose first Terac page was a deep link into the dashboard, which is
// exactly what an agent hand-off is, was recorded with no landing context at
// all. The MCP tools return absolute /{orgSlug}/... URLs and the operating
// guide tells the agent to hand them over, so that is not an edge case.
const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid", // Google Ads
  "fbclid", // Meta Ads
  "li_fat_id", // LinkedIn Ads
] as const;

const ATTRIBUTION_FLAG_KEY = "terac_initial_attribution_set";

function readFlag(): boolean {
  try {
    return Boolean(window.localStorage.getItem(ATTRIBUTION_FLAG_KEY));
  } catch {
    return false;
  }
}

function writeFlag(): void {
  try {
    window.localStorage.setItem(ATTRIBUTION_FLAG_KEY, "1");
  } catch {
    // localStorage can throw in private/incognito mode; the cost of falling
    // back to "fire on every nav" is one /engage/ POST per page until they
    // sign in, which posthog's $set_once still no-ops at the server.
  }
}

export function FirstTouchAttribution() {
  const posthog = usePostHog();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) {
      return;
    }
    fired.current = true;

    if (readFlag()) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const initial: Record<string, string> = {};

    for (const key of TRACKED_PARAMS) {
      const value = params.get(key);
      if (value) {
        initial[`initial_${key}`] = value;
      }
    }

    const referrer = document.referrer;
    if (referrer) {
      try {
        const referrerHost = new URL(referrer).host;
        if (referrerHost && referrerHost !== window.location.host) {
          initial.initial_referrer = referrer;
          initial.initial_referrer_host = referrerHost;
        }
      } catch {
        // ignore malformed referrer
      }
    }

    initial.initial_landing_path = window.location.pathname;

    posthog.setPersonProperties(undefined, initial);
    writeFlag();
  }, [posthog]);

  return null;
}
